import { useState, useMemo, useRef, useEffect } from "react"
import ExistingContact from "components/admin/RegistrationContact/existing_contact"
import EditContact from "components/admin/RegistrationContact/edit_contact"
import axios from "axios"
import useLocalStorageState from "hooks/use_local_storage_state"
import { string, arrayOf, shape, func, bool } from "prop-types"
import _ from "underscore"

const RegistrationContact = ({
  responsiblePerson: existingContact,
  editRegistrationContactUrl,
  onSelect,
  onCancelClick,
  isAdmin,
  hasNamedAttendees = true,
  helpText = null,
  suggestedPeople = [],
  editMode: editModeFromProps,
  isSaving = false,
  isClearable = false
}) => {
  const [editMode, setEditMode] = useState(editModeFromProps)
  const [showErrors, setShowErrors] = useState(false)
  const [errors, setErrors] = useState([])
  const [responsiblePerson, setResponsiblePerson] = useLocalStorageState(
    editRegistrationContactUrl,
    existingContact
  )
  const [updating, setUpdating] = useState(false)
  const [suggestedPeopleList, setSuggestedPeopleList] = useState(
    suggestedPeople.filter(p => {
      return p?.accountCenterId || p?.account_center_id
    })
  )
  const mode = useMemo(() => {
    if (editMode) {
      return "edit"
    } else if (responsiblePerson) {
      return "show"
    } else {
      return "new"
    }
  }, [responsiblePerson, editMode])

  useEffect(() => {
    setResponsiblePerson(existingContact)
  }, [existingContact])

  useEffect(() => {
    if (suggestedPeople.length > 0) {
      setSuggestedPeopleList(
        _.uniq(
          [
            ...suggestedPeopleList,
            ...suggestedPeople.filter(p => {
              return p?.accountCenterId || p?.account_center_id
            })
          ],
          "accountCenterId"
        )
      )
    }
  }, [suggestedPeople])

  const editContact = useRef(null)

  useEffect(() => {
    setEditMode(editModeFromProps)
  }, [editModeFromProps])

  const addPersonToSuggestList = person => {
    if (person) {
      setSuggestedPeopleList(_.uniq([person, ...suggestedPeopleList], "accountCenterId"))
    } else {
      setSuggestedPeopleList([])
    }
  }

  const handleEditCancelClick = () => {
    if (onCancelClick) {
      onCancelClick(responsiblePerson)
    }
    setErrors([])
    setShowErrors(false)
  }

  const handleEditSelect = person => {
    if (onSelect) {
      setUpdating(false)
      addPersonToSuggestList(person)
      setResponsiblePerson(person)
      return onSelect(person)
    }

    setUpdating(true)
    axios
      .put(
        editRegistrationContactUrl,
        {
          new_person_account_center_id: person?.account_center_id
        },
        {
          "Content-Type": "application/json",
          headers: {
            "X-CSRF-Token": $.rails.csrfToken()
          }
        }
      )
      .then(response => {
        setUpdating(false)
        setResponsiblePerson(response.data.person)
      })
      .catch(error => {
        if (error) {
          setErrors(error.response.data.errors)
          setShowErrors(true)
        }
        setUpdating(false)
        editContact.current.clearSelection()
      })
  }

  const handleClear = () => {
    handleEditSelect(null)
    setEditMode(false)
  }

  const renderMode = mode => {
    switch (mode) {
      case "edit":
        return (
          <EditContact
            onCancel={handleEditCancelClick}
            onSelect={handleEditSelect}
            loading={updating}
            ref={editContact}
            helpText={helpText}
            suggestedPeople={hasNamedAttendees ? suggestedPeopleList : []}
            responsiblePerson={responsiblePerson}
            isSaving={isSaving}
          />
        )
      case "show":
        return (
          <ExistingContact
            responsiblePerson={responsiblePerson}
            isAdmin={isAdmin}
            isClearable={isClearable}
            onClear={handleClear}
          />
        )
      case "new":
        return (
          <EditContact
            onSelect={handleEditSelect}
            loading={updating}
            ref={editContact}
            helpText={helpText}
            suggestedPeople={hasNamedAttendees ? suggestedPeopleList : []}
          />
        )
      default:
        return null
    }
  }

  return (
    <div>
      {renderMode(mode)}
      {showErrors && (
        <ul
          className="mt-1 mb-0 unstyled p-1 error-alert quiet-alert alert"
          css={{ flexBasis: "100%" }}
        >
          {errors.map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

RegistrationContact.propTypes = {
  responsiblePerson: shape(),
  editRegistrationContactUrl: string,
  onSelect: func,
  onChangeClick: func,
  onCancelClick: func,
  isAdmin: bool,
  hasNamedAttendees: bool,
  helpText: string,
  suggestedPeople: arrayOf(shape()),
  editMode: bool,
  isSaving: bool,
  isClearable: bool
}

export default RegistrationContact
